import React from "react";
import Navigation from "@/layout/navigation";

export default function TermsConditions() {
  return (
    <>
      {/* Header avec navigation */}
      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation noMenu />
        <div className="flex flex-col md:items-center md:justify-center gap-4 mt-6 md:mt-10 z-10 md:text-center">
          <h1 className="text-4xl md:text-5xl font-medium leading-tight">
            Terms & Conditions
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-600">
            These terms govern your use of Featcal and outline our mutual rights
            and responsibilities.
          </p>
        </div>
      </header>

      {/* Contenu principal - ultra simple */}
      <div className="bg-white py-20 px-8 xl:px-0">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-12 text-lg leading-relaxed text-gray-800">
            <div className="text-sm text-blue-600 font-medium">
              Last updated: September 28, 2025
            </div>

            <div className="space-y-8">
              <p>
                Welcome to Featcal. These Terms and Conditions
                (&quot;Terms&quot;) govern your access to and use of our
                platform, website (featcal.com), mobile applications, and
                related services (collectively, the &quot;Services&quot;)
                provided by Featcal (&quot;we,&quot; &quot;our,&quot; or
                &quot;us&quot;).
              </p>

              <p>
                By accessing or using our Services, you agree to be bound by
                these Terms. If you do not agree to these Terms, you may not
                access or use our Services.
              </p>

              <p className="font-semibold">
                Please read these Terms carefully before using our Services.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                1. Acceptance of Terms
              </h2>

              <p>
                By creating an account, accessing, or using Featcal, you
                acknowledge that you have read, understood, and agree to be
                bound by these Terms.
              </p>
              <p>
                You also agree to comply with our Privacy Policy, which is
                incorporated by reference into these Terms.
              </p>
              <p>
                If you are using our Services on behalf of an organization, you
                represent that you have the authority to bind that organization
                to these Terms.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                2. Description of Service
              </h2>

              <p>
                Featcal is a digital platform designed for healthcare
                professionals, nutritionists, and coaches to:
              </p>

              <p>Track and monitor patient/client health and wellness data</p>
              <p>
                Create and manage meal plans, workout plans, and consultation
                notes
              </p>
              <p>
                Facilitate secure communication between professionals and their
                patients/clients
              </p>
              <p>Integrate with third-party health devices and platforms</p>
              <p>Generate automated reports and insights using AI technology</p>
              <p>Provide a Progressive Web App (PWA) for easy access</p>

              <p>
                Our Services are intended for use by licensed healthcare
                professionals, certified nutritionists, qualified coaches, and
                their patients/clients.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                3. User Accounts and Eligibility
              </h2>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  3.1 Eligibility
                </h3>
                <p>You must be at least 18 years old to use our Services</p>
                <p>
                  Professionals must hold valid licenses, certifications, or
                  qualifications in their respective fields
                </p>
                <p>
                  You must provide accurate and complete information when
                  creating your account
                </p>
                <p>
                  You are responsible for maintaining the confidentiality of
                  your account credentials
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  3.2 Account Security
                </h3>
                <p>
                  You are responsible for all activities that occur under your
                  account
                </p>
                <p>
                  You must notify us immediately of any unauthorized use of your
                  account
                </p>
                <p>
                  We reserve the right to suspend or terminate accounts that
                  violate these Terms
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                4. Acceptable Use
              </h2>

              <p>
                You agree to use our Services only for lawful purposes and in
                accordance with these Terms.
              </p>

              <p className="font-medium">You agree NOT to:</p>

              <p>
                Use the Services for any illegal, harmful, or unauthorized
                purpose
              </p>
              <p>
                Share your account credentials with unauthorized individuals
              </p>
              <p>Upload or transmit viruses, malware, or other harmful code</p>
              <p>
                Attempt to gain unauthorized access to our systems or other
                users&apos; accounts
              </p>
              <p>Use the Services to spam, harass, or abuse other users</p>
              <p>
                Reverse engineer, decompile, or disassemble any part of our
                Services
              </p>
              <p>
                Use automated tools to access our Services without permission
              </p>
              <p>
                Violate any applicable laws, regulations, or professional
                standards
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                5. Professional Responsibilities
              </h2>

              <p>
                If you are a healthcare professional, nutritionist, or coach
                using our Services:
              </p>

              <p>
                You remain solely responsible for all professional decisions and
                patient/client care
              </p>
              <p>
                You must comply with all applicable professional standards,
                ethics, and regulations
              </p>
              <p>
                You are responsible for obtaining proper consent from
                patients/clients
              </p>
              <p>
                You must maintain appropriate professional liability insurance
              </p>
              <p>
                Featcal does not provide medical advice and is not a substitute
                for professional judgment
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                6. Data and Privacy
              </h2>

              <p>
                Our collection, use, and protection of your data is governed by
                our Privacy Policy
              </p>
              <p>
                You retain ownership of the health and personal data you input
                into our Services
              </p>
              <p>
                You grant us a limited license to process your data to provide
                our Services
              </p>
              <p>
                You are responsible for ensuring you have proper consent to
                share patient/client data
              </p>
              <p>
                We implement industry-standard security measures to protect your
                data
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                7. Payment Terms
              </h2>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  7.1 Subscription Fees
                </h3>
                <p>Access to our Services requires a paid subscription</p>
                <p>
                  Subscription fees are based on the number of patients/clients
                  and features used
                </p>
                <p>
                  All fees are stated in Canadian dollars (CAD) unless otherwise
                  specified
                </p>
                <p>
                  Setup fees may apply based on your specific configuration
                  needs
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  7.2 Billing and Payment
                </h3>
                <p>
                  Subscription fees are billed monthly or annually, as selected
                </p>
                <p>Payment is due in advance for each billing period</p>
                <p>
                  We accept major credit cards and other payment methods as
                  available
                </p>
                <p>Late payments may result in service suspension</p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  7.3 Refunds and Cancellation
                </h3>
                <p>You may cancel your subscription at any time</p>
                <p>
                  Cancellation takes effect at the end of your current billing
                  period
                </p>
                <p>We offer a 30-day free trial for new customers</p>
                <p>
                  Refunds are provided at our discretion for specific
                  circumstances
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                8. Intellectual Property
              </h2>

              <p>
                Our Services, including software, content, trademarks, and other
                materials, are owned by Featcal and protected by intellectual
                property laws
              </p>
              <p>
                You are granted a limited, non-exclusive license to use our
                Services during your subscription
              </p>
              <p>
                You may not copy, modify, distribute, or create derivative works
                of our Services
              </p>
              <p>
                You retain ownership of the content and data you create using
                our Services
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                9. Third-Party Integrations
              </h2>

              <p>
                Our Services may integrate with third-party devices and
                platforms (such as InBody, Seca, Omron, Tanita)
              </p>
              <p>
                These integrations are subject to the terms and conditions of
                the respective third parties
              </p>
              <p>
                We are not responsible for the availability, accuracy, or
                functionality of third-party services
              </p>
              <p>
                You are responsible for complying with third-party terms when
                using integrations
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                10. Disclaimers and Limitation of Liability
              </h2>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  10.1 Service Disclaimer
                </h3>
                <p>
                  Our Services are provided &quot;as is&quot; without warranties
                  of any kind
                </p>
                <p>
                  We do not guarantee that our Services will be error-free or
                  uninterrupted
                </p>
                <p>
                  Featcal is a tool to assist professionals and is not a
                  substitute for professional judgment
                </p>
                <p>We do not provide medical, nutritional, or fitness advice</p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  10.2 Limitation of Liability
                </h3>
                <p>
                  To the maximum extent permitted by law, Featcal shall not be
                  liable for any indirect, incidental, special, or consequential
                  damages
                </p>
                <p>
                  Our total liability to you shall not exceed the amount you
                  paid for our Services in the 12 months preceding the claim
                </p>
                <p>
                  You acknowledge that professional liability remains with the
                  healthcare provider, not with Featcal
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                11. Indemnification
              </h2>

              <p>
                You agree to indemnify and hold harmless Featcal from any
                claims, damages, or expenses arising from:
              </p>

              <p>Your use of our Services</p>
              <p>Your violation of these Terms</p>
              <p>
                Your violation of any applicable laws or professional standards
              </p>
              <p>
                Any claims related to patient/client care or professional
                services you provide
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                12. Termination
              </h2>

              <p>Either party may terminate these Terms at any time</p>
              <p>
                We may suspend or terminate your account immediately if you
                violate these Terms
              </p>
              <p>Upon termination, you will lose access to our Services</p>
              <p>
                You may request export of your data within 30 days of
                termination
              </p>
              <p>
                Provisions regarding liability, indemnification, and
                intellectual property survive termination
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                13. Governing Law and Disputes
              </h2>

              <p>These Terms are governed by the laws of Quebec and Canada</p>
              <p>
                Any disputes will be resolved in the courts of Quebec, Canada
              </p>
              <p>
                We encourage resolution of disputes through direct communication
                before legal action
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                14. Changes to Terms
              </h2>

              <p>We may update these Terms from time to time</p>
              <p>Changes will be posted on this page with the updated date</p>
              <p>
                Significant changes will be communicated through email or in-app
                notifications
              </p>
              <p>
                Your continued use of our Services after changes constitutes
                acceptance of the new Terms
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                15. Contact Information
              </h2>

              <p>
                If you have questions about these Terms or our Services, please
                contact us:
              </p>

              <p className="font-semibold">Featcal</p>
              <p>Email: legal@featcal.com</p>
              <p>Website: https://featcal.com</p>
              <p>Address: [Insert Business Address]</p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                16. Entire Agreement
              </h2>

              <p>
                These Terms, together with our Privacy Policy, constitute the
                entire agreement between you and Featcal
              </p>
              <p>
                If any provision of these Terms is found to be unenforceable,
                the remaining provisions will remain in effect
              </p>
              <p>
                Our failure to enforce any provision does not waive our right to
                enforce it later
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
